<!--Copyright (c) 2024 FileCloud. All rights Reserved.-->
<template>
  <VDropdown
    ref="popover"
    :popper-class="popoverClass"
    :class="'sample'"
    :container="container"
    :placement="placement"
    :distance="offset"
    :shown="open"
    @show="
      () => {
        isOpen = true;
        $emit('show', true);
      }
    "
    @hide="
      () => {
        isOpen = false;
        $emit('hide', true);
      }
    "
    @keydown.space="handleKeydownOpen"
    @keydown.enter="handleKeydownOpen"
  >
    <slot>
      <div ref="trigger">
        <Icon 
          :name="triggerBtn"
          :family="triggerIconFamily"
          :class="triggerBtnClass"
          :aria-label="labelAria"
          tabindex="0"
          role="button"
          @keydown.space="handleKeydownOpen"
          @keydown.enter="handleKeydownOpen"
          @focus="$emit('focus')"
          @blur="$emit('blur')"
        />
      </div>
    </slot>
    <template #popper>
      <div ref="dropdown" class="dropdown-wrapper">
        <div v-for="(item, index) in listItems" :key="item.id">
          <div v-if="item.isTitle === true" class="dropdown-item-title">
            {{ $t(item.name) }}
          </div>
          <div
            v-else-if="!(isItemHidden(item) || item.isTitle)"
            :key="index"
            class="dropdown-item"
            role="button"
            tabindex="0"
            :class="{
              'has-seperator': hasItemSeparator(item),
              'dropdown-item--disabled': isItemDisabled(item),
              [`dropdown-item-focus-${index}`]: true,
              [item.id]: item.id,
            }"
            @click="triggerAction(item.action, item[paramName])"
            @keydown.space.prevent="triggerAction(item.action, item[paramName])"
            @keydown.enter.prevent="triggerAction(item.action, item[paramName])"
          >
            <component
              v-if="isFileSource(item.icon)"
              :is="getFileSource(item.icon)"
              class="dropdown-item-file-icon"
            /> 
            <Icon
              v-else-if="item.icon"
              :name="item.icon"
              :family="item.iconFamily || 'fal'"
            />
            <span class="popover-text"
              >{{
                $t(
                  typeof item.name === 'string' ? item.name : item.name(target),
                )
              }}
              <span
                v-if="item.shortKey"
                class="shortkey float-right text-capitalize"
              >
                {{ item.shortKey }}</span
              >
            </span>
            <span  v-if="item.isBeta" class="new-tag float-right">
              {{ $t('BETA') }}
            </span>
            <div
              v-if="item.counter"
              class="dropdown-item__counter badge badge-primary"
            >
              {{ item.counter }}
            </div>
          </div>
        </div>
        <slot name="after"></slot>
      </div>
    </template>
  </VDropdown>
</template>

<script>
import Icon from './Icon';
import { createFocusTrap } from 'focus-trap';
import { isMobileDevice } from '../utils/responsive.js';

const requireSvg = require.context('../assets');

export default {
  name: 'DropDownList',
  components: {
    Icon,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    target: {
      type: Object,
      default: () => ({}),
    },
    offset: {
      type: Number,
      default: 3,
    },
    placement: {
      type: String,
      default: 'bottom',
    },
    labelAria: {
      type: String,
      default: '',
    },
    paramName: {
      type: String,
      default: '',
    },
    actionName: {
      type: String,
      default: '',
    },
    hasCustomList: {
      type: Boolean,
      default: false,
      required: false,
    },
    open: {
      type: Boolean,
      default: false,
      required: false,
    },
    triggerBtn: {
      type: String,
      default: 'plus',
    },
    dropdownClass: {
      type: String,
      default: '',
    },
    triggerBtnClass: {
      type: String,
      default: 'trigger-btn',
    },
    triggerIconFamily: {
      type: String,
      default: 'fas',
    },
    container: {
      type: String,
      default: 'body',
    },
  },
  emits: ['show', 'hide', 'focus', 'blur', 'change', 'triggerDDAction'],
  data() {
    return {
      isOpen: false,
      focusTrap: null,
      svgItems: requireSvg.keys().map((filePath) => {
        const svgComponent = requireSvg(filePath);
        return {
          path: filePath,
          img: svgComponent.default || svgComponent, 
        };
      }),
    };
  }, 
  computed: {
    listItems() {
      return this.items.filter((item) => {
        return this.hasCustomList ? !item.listOnly : item;
      });
    },
    popoverClass() {
      return 'dropdown ' + this.dropdownClass;
    },
    isMobileDevice,
  },
  watch: {
    isOpen(oppened) {
      this.$emit('change', oppened);
      if (oppened && !this.isMobileDevice) {
        this.$emit('change', true);
        this.handleFocusTrapInit();
      } else {
        if (this.focusTrap) this.focusTrap.deactivate();
      }
    },
  },
  beforeUnmount() {
    if (this.focusTrap) this.focusTrap.deactivate();
  },
  methods: {
    isItemDisabled(item) {
      if (!item.disabled) return false; // boolean false or doesn't exist
      if (typeof item.disabled === 'boolean') return true;
      if (this.target && item.disabled(this.target)) return true;

      return false;
    },
    isItemHidden(item) {
      if (!item.hidden) return false; // boolean false or doesn't exist
      if (typeof item.hidden === 'boolean') return true;
      if (this.target && item.hidden(this.target)) return true;

      return false;
    },
    hasItemSeparator(item) {
      if (!item.seperator || !item.conditionalSeperator) return false; // boolean false or doesn't exist
      if (typeof item.seperator === 'boolean') return true;
      if (item.conditionalSeperator && item.conditionalSeperator(this.target))
        return true;

      return false;
    },
    triggerAction(action, param) {
      this.$refs.popover.hide();

      if (this.actionName) {
        this.$emit('triggerDDAction', this.actionName, param);
      } else {
        this.$emit('triggerDDAction', action, param);
      }
    },
    handleFocusTrapInit() {
      // timeout needed due to v-popover delay
      setTimeout(() => {
        if (!this.focusTrap) {
          const initialFocus = this.listItems.length
            ? '.dropdown-wrapper [class*="dropdown-item-focus-"]'
            : undefined;

          this.focusTrap = createFocusTrap(this.$refs.dropdown, {
            allowOutsideClick: true,
            escapeDeactivates: false,
            returnFocusOnDeactivate: false,
            initialFocus: initialFocus,
          });
        } else {
          this.focusTrap.updateContainerElements(this.$refs.dropdown);
        }
        this.focusTrap?.activate();
      }, 400);
    },
    handleKeydownOpen(event) {
      event.stopPropagation();
      event.preventDefault();
      event.target.click();
    },
    isFileSource(icon) {
      if(!icon) return false;
      const imageFormats = ['.svg', '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'];
      return imageFormats.some(format => icon.endsWith(format));
    },
    getFileSource(icon) { 
      const file = this.svgItems.find((item) => item.path.includes(icon));
      if (file) {
        return file.img;
      } 
    }
  },
};
</script>
